// housekeeping/components/WorkersPage.js
import React from 'react';
import { User } from 'lucide-react';
import { styles } from '../styles/styles.js';
import { getWorkerStats, getWorkerStatusColor, getStatusColor } from '../utils/utils.js';

const WorkersPage = ({ workers, tasks, dateFilter, setDateFilter }) => {
    return (
        <div>
            {/* Date Filter for Workers */}
            <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Filter by Date:</label>
                    <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={styles.filterInput}
                    />
                    <button
                        onClick={() => setDateFilter('')}
                        style={{
                            padding: '0.5rem 0.75rem',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Workers Grid */}
            <div style={styles.workersGrid}>
                {workers.map(worker => {
                    const stats = getWorkerStats(worker.id, tasks);
                    const workerTasks = tasks.filter(task =>
                        task.assignedTo === worker.id &&
                        (!dateFilter || task.dueDate.includes(dateFilter))
                    );
                    const statusColors = getWorkerStatusColor(worker.status);

                    return (
                        <div key={worker.id} style={styles.workerCard}>
                            <div style={styles.workerHeader}>
                                <div style={styles.workerInfo}>
                                    <div style={{ ...styles.avatar, width: '2rem', height: '2rem', fontSize: '0.8rem' }}>
                                        {worker.initials}
                                    </div>
                                    <div>
                                        <h3 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.25rem' }}>{worker.name}</h3>
                                        <p style={{ color: '#6b7280' }}>{worker.role}</p>
                                    </div>
                                </div>
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem',
                                    fontSize: '0.675rem',
                                    fontWeight: '600',
                                    backgroundColor: statusColors.bg,
                                    color: statusColors.color
                                }}>
                                    {worker.status}
                                </span>
                            </div>

                            {/* Worker Stats */}
                            <div style={styles.workerStats}>
                                <div>
                                    <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>
                                        {dateFilter ? workerTasks.length : stats.total}
                                    </p>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total</p>
                                </div>
                                <div>
                                    <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#16a34a', marginBottom: '0.25rem' }}>
                                        {dateFilter ? workerTasks.filter(t => t.status === 'completed').length : stats.completed}
                                    </p>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Done</p>
                                </div>
                                <div>
                                    <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#d97706', marginBottom: '0.25rem' }}>
                                        {dateFilter ? workerTasks.filter(t => t.status === 'active').length : stats.active}
                                    </p>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Active</p>
                                </div>
                                <div>
                                    <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#dc2626', marginBottom: '0.25rem' }}>
                                        {dateFilter ? workerTasks.filter(t => t.priority === 'high').length : stats.high}
                                    </p>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>High</p>
                                </div>
                            </div>

                            {/* Completion Rate */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Completion Rate</span>
                                    <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                                        {dateFilter && workerTasks.length > 0 ?
                                            Math.round((workerTasks.filter(t => t.status === 'completed').length / workerTasks.length) * 100) :
                                            stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0
                                        }%
                                    </span>
                                </div>
                                <div style={styles.progressBar}>
                                    <div
                                        style={{
                                            ...styles.progressFill,
                                            width: `${dateFilter && workerTasks.length > 0 ?
                                                (workerTasks.filter(t => t.status === 'completed').length / workerTasks.length) * 100 :
                                                stats.total > 0 ? (stats.completed / stats.total) * 100 : 0
                                                }%`
                                        }}
                                    ></div>
                                </div>
                            </div>

                            {/* Current Tasks */}
                            <div>
                                <h4 style={{ fontWeight: '600', fontSize: '0.875rem', color: '#111827', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <User size={16} />
                                    Current Tasks
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {(dateFilter ? workerTasks : tasks.filter(task => task.assignedTo === worker.id))
                                        .slice(0, 10)
                                        .map(task => {
                                            const statusColors = getStatusColor(task.status);
                                            return (
                                                <div key={task.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                                                    <div>
                                                        <p style={{ fontWeight: '500', color: '#111827', marginBottom: '0.25rem' }}>{task.title}</p>
                                                        <p style={{ fontSize: '0.775rem', color: '#6b7280' }}>{task.location}</p>
                                                    </div>
                                                    <span style={{

                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '9999px',
                                                        fontSize: '0.675rem',
                                                        fontWeight: '600',
                                                        backgroundColor: statusColors.bg,
                                                        color: statusColors.color
                                                    }}>
                                                        {/*  */}
                                                        {task.status === 'active' ? 'Active' :
                                                            task.status === 'completed' ? 'Done' : 'Pending'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    {(dateFilter ? workerTasks : tasks.filter(task => task.assignedTo === worker.id)).length === 0 && (
                                        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No tasks assigned</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WorkersPage;